<template>
	<nav class="mobile-bottom-nav" :aria-label="$t('nav.portal-navigation')">
		<button
			v-for="link in primaryLinks"
			:key="link.route"
			type="button"
			class="mobile-nav-action"
			:class="{ 'is-active': isActive(link) }"
			:aria-current="isActive(link) ? 'page' : null"
			@click="handleClick(link)"
		>
			<custom-page-icon v-if="link.customIcon" :name="link.icon" :size="22"></custom-page-icon>
			<portal-icon v-else :name="link.icon" :size="22"></portal-icon>
			<span>{{ link.label }}</span>
		</button>

		<v-menu v-model="menuOpen" top left offset-y content-class="mobile-more-surface" :close-on-content-click="false">
			<template v-slot:activator="{ on, attrs }">
				<button
					type="button"
					class="mobile-nav-action"
					:class="{ 'is-active': overflowIsActive }"
					:aria-label="$t('global.more')"
					v-bind="attrs"
					v-on="on"
				>
					<portal-icon name="menu" :size="22"></portal-icon>
					<span>{{ $t('global.more') }}</span>
				</button>
			</template>

			<div class="mobile-more-menu">
				<div class="mobile-destination-list">
					<template v-for="link in allLinks">
						<button
							type="button"
							class="mobile-destination"
							:class="{ 'is-active': isActive(link) }"
							:key="link.route || link.hyperLink"
							@click="handleClick(link)"
						>
							<custom-page-icon v-if="link.customIcon" :name="link.icon" :size="21"></custom-page-icon>
							<portal-icon v-else :name="link.icon" :size="21"></portal-icon>
							<span>{{ link.label }}</span>
							<portal-icon v-if="link.hyperLink" name="external-link" :size="16"></portal-icon>
						</button>
						<button
							v-for="tab in link.tabs || []"
							type="button"
							class="mobile-subdestination"
							:class="{ 'is-active': $route.fullPath === tab.route }"
							:key="`${link.route}-${tab.route}`"
							@click="goTo(tab.route)"
						>
							{{ tab.label }}
						</button>
					</template>
				</div>

				<div class="mobile-settings">
					<settings-nav></settings-nav>
				</div>

				<button type="button" class="mobile-logout" @click="confirmLogout">
					<portal-icon name="log-out" :size="20"></portal-icon>
					<span>{{ $t('nav.logout') }}</span>
				</button>
			</div>
		</v-menu>
	</nav>
</template>

<script>
	import CustomPageIcon from '@/components/CustomPageIcon.vue';
	import SettingsNav from '@/views/nav/settings/SettingsNav.vue';
	import ConfirmModal from '@/components/ConfirmModal.vue';
	import AuthService from '@/modules/auth/AuthService';
	import PodService from '@/services/PodService';
	import NavMixin from '@/views/nav/NavMixin';
	import PortalIcon from '@/components/PortalIcon.vue';

	export default {
		name: 'MobileNav',

		components: { CustomPageIcon, SettingsNav, PortalIcon },

		mixins: [NavMixin],

		data() {
			return {
				menuOpen: false,
			};
		},

		watch: {
			menuOpen(isOpen) {
				document.documentElement.classList.toggle('portal-mobile-menu-open', isOpen);
			},
		},

		beforeDestroy() {
			document.documentElement.classList.remove('portal-mobile-menu-open');
		},

		computed: {
			homeLink() {
				return {
					label: this.$t('nav.home'),
					icon: 'home',
					route: '/welcome',
				};
			},

			allLinks() {
				return [this.homeLink, ...this.navLinks];
			},

			primaryLinks() {
				const preferredRoots = ['/projects', '/invoices'];
				const preferred = preferredRoots
					.map((root) => this.navLinks.find((link) => link.route && link.route.startsWith(root)))
					.filter(Boolean);
				return [this.homeLink, ...preferred];
			},

			overflowIsActive() {
				return !this.primaryLinks.some((link) => this.isActive(link));
			},
		},

		methods: {
			isActive(link) {
				if (!link.route) {
					return false;
				}
				return [link.route, ...(link.tabs || []).map((tab) => tab.route)].some((route) => {
					const routePath = route.split('?')[0];
					return this.$route.path === routePath || this.$route.path.startsWith(`${routePath}/`);
				});
			},

			handleClick(link) {
				if (link.hyperLink) {
					window.open(link.hyperLink, '_blank', 'noopener');
					return;
				}
				this.goTo(link.route);
			},

			goTo(route) {
				this.menuOpen = false;
				if (route && this.$route.fullPath !== route) {
					this.$router.push(route);
				}
			},

			confirmLogout() {
				const binding = {
					icon: 'logout',
					severity: 'info',
					headingText: this.$t('nav.logout-confirm'),
				};
				this.$store.state.globalModalController.openModal(ConfirmModal, binding).then((confirmed) => {
					if (confirmed) {
						this.doLogout();
					}
				});
			},

			doLogout() {
				try {
					window.google.accounts.id.disableAutoSelect();
				} catch (error) {
					// Google One Tap is optional.
				}

				AuthService.logout()
					.then(this.podLogout)
					.catch(this.podLogout);
			},

			podLogout() {
				const podService = new PodService();
				podService
					.clearCookies()
					.finally(() => {
						this.$store.commit('logOut');
						location.reload();
					});
			},
		},
	};
</script>

<style lang="scss">
	.mobile-more-surface {
		width: calc(100vw - 24px);
		max-width: 420px;
		max-height: calc(100vh - 112px);
		overflow: auto;
		border: 1px solid var(--portal-border);
		border-radius: var(--portal-radius-md) !important;
		background: var(--portal-surface);
		box-shadow: 0 18px 44px rgba(16, 17, 15, 0.18) !important;
	}
</style>

<style scoped lang="scss">
	.mobile-bottom-nav {
		position: relative;
		z-index: 25;
		min-height: 68px;
		flex: 0 0 auto;
		align-items: stretch;
		justify-content: space-around;
		padding: 6px max(8px, env(safe-area-inset-right)) max(6px, env(safe-area-inset-bottom))
			max(8px, env(safe-area-inset-left));
		border-top: 1px solid var(--portal-border);
		background: var(--portal-surface);
	}

	.mobile-nav-action {
		min-width: 68px;
		display: grid;
		place-items: center;
		align-content: center;
		gap: 3px;
		padding: 5px 8px;
		border: 0;
		border-radius: var(--brand-button-radius, 10px);
		background: transparent;
		color: var(--portal-muted);
		font-size: 11px;
		font-weight: 500;
	}

	.mobile-nav-action.is-active {
		color: var(--v-primary-base);
	}

	.mobile-more-menu {
		background: var(--portal-surface);
		color: var(--portal-text);
	}

	.mobile-destination-list {
		padding: 8px;
	}

	.mobile-destination,
	.mobile-subdestination,
	.mobile-logout {
		width: 100%;
		display: flex;
		align-items: center;
		border: 0;
		background: transparent;
		color: var(--portal-text);
		text-align: left;
	}

	.mobile-destination {
		min-height: 44px;
		gap: 12px;
		padding: 8px 10px;
		border-radius: var(--brand-button-radius, 10px);
		font-weight: 500;
	}

	.mobile-destination span {
		min-width: 0;
		flex: 1;
	}

	.mobile-subdestination {
		min-height: 38px;
		padding: 7px 10px 7px 44px;
		border-radius: var(--brand-button-radius, 8px);
		color: var(--portal-muted);
	}

	.mobile-destination.is-active,
	.mobile-subdestination.is-active {
		background: var(--v-gray_20-base);
		color: var(--v-primary-base);
	}

	.mobile-settings {
		border-top: 1px solid var(--portal-border);
		border-bottom: 1px solid var(--portal-border);
	}

	.mobile-logout {
		min-height: 48px;
		gap: 12px;
		padding: 10px 18px;
		color: var(--v-error-base);
		font-weight: 500;
	}
</style>
